import React, { useMemo, useState } from 'react';

import { CUSTOM_WORKFLOW_TYPE_LABEL } from '@aline/common';
import { Autocomplete, TextField, Typography, useTheme } from '@mui/material';
import type { GridFilterInputValueProps } from '@mui/x-data-grid-premium';
import clsx from 'clsx';
import { FilePen, LayoutTemplate } from 'lucide-react';
import { AlineCheckbox, FilterTags } from 'src/components';
import { usePreserveAutocompleteSearchInput } from 'src/components/generic/data-grid/autocomplete-filter-utils';
import { getIsCustomWorkflowTypeOption } from 'src/components/workflow/workflow-list/workflow-type';
import type { WorkflowTypeFilterOption } from 'src/components/workflow/workflow-list/workflow-type';
import { Apollo } from 'src/gql/generated';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  autocomplete: {
    width: 500,
  },
  item: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
  },
}));

const CUSTOM_OPTION = {
  __typename: 'CustomWorkflowType',
  id: 'custom',
  label: CUSTOM_WORKFLOW_TYPE_LABEL,
} as const;

function getOptionKey(option: WorkflowTypeFilterOption): string {
  return option.id;
}

function getOptionLabel(option: WorkflowTypeFilterOption): string {
  return option.label;
}

export function WorkflowTypeAutocompleteFilter({
  applyValue,
  item,
}: GridFilterInputValueProps): JSX.Element {
  const theme = useTheme();
  const { classes } = useStyles();
  const [truncateTags, setTruncateTags] = useState(true);
  const [value, setValue] = useState<WorkflowTypeFilterOption[]>(item.value ?? []);
  const { inputValue, onInputChange } = usePreserveAutocompleteSearchInput();
  const { data, loading } = Apollo.useGetWorkflowTypesQuery();

  const options = useMemo(
    (): WorkflowTypeFilterOption[] => [CUSTOM_OPTION, ...(data?.workflowTypes ?? [])],
    [data?.workflowTypes]
  );

  function handleValueChange(nextValue: WorkflowTypeFilterOption[]): void {
    applyValue({ ...item, value: nextValue });
    setValue(nextValue);
  }

  return (
    <Autocomplete<WorkflowTypeFilterOption, true, false, false>
      disableCloseOnSelect
      fullWidth
      multiple
      openOnFocus
      blurOnSelect={false}
      className={classes.autocomplete}
      getOptionKey={getOptionKey}
      getOptionLabel={getOptionLabel}
      inputValue={inputValue}
      isOptionEqualToValue={(option, selected) => getOptionKey(option) === getOptionKey(selected)}
      loading={loading}
      options={options}
      renderInput={params => (
        <TextField placeholder="Search workflow types..." {...params} variant="standard" />
      )}
      renderOption={(props, option, state) => {
        const { className, key, ...otherProps } = props;
        const Icon = getIsCustomWorkflowTypeOption(option) ? FilePen : LayoutTemplate;
        return (
          <li key={key} {...otherProps} className={clsx(className, classes.item)}>
            <AlineCheckbox checked={state.selected} style={{ marginRight: theme.spacing(1) }} />
            <Icon size={16} />
            <Typography>{getOptionLabel(option)}</Typography>
          </li>
        );
      }}
      renderValue={tags => (
        <FilterTags<WorkflowTypeFilterOption>
          getTextFromTag={getOptionLabel}
          tags={tags}
          truncateTags={truncateTags}
          onDelete={tag =>
            handleValueChange(
              tags.filter(filterTag => getOptionKey(filterTag) !== getOptionKey(tag))
            )
          }
        />
      )}
      slotProps={{
        popper: {
          modifiers: [
            { enabled: false, name: 'flip' },
            { enabled: false, name: 'preventOverflow' },
          ],
        },
      }}
      value={value}
      onBlur={e => {
        e.stopPropagation();
        setTruncateTags(true);
      }}
      onChange={(_e, nextValue) => handleValueChange(nextValue)}
      onFocus={e => {
        e.stopPropagation();
        setTruncateTags(false);
      }}
      onInputChange={onInputChange}
    />
  );
}
